import React from "react"
import { Box } from "@mui/material"
import { deepPurple, pink } from '@mui/material/colors'
import { AppButtonIcon } from "@components"
import { ANALYZERS_ROUTES, BACKEND_APIS, ONE_WAY_ROUTES, REAL_TIME_ROUTES } from "@constants"
import { REPORT_ACTIONS } from "Pages/ReportsAlerts/Reports/@constants"
import { getExcel } from "@ui-seeker/utils"
import { useReportActions } from "Pages/ReportsAlerts/Reports/@hooks"

export default function AppLinksBlock(props = {}) {
	const {
		lor = true,
		dor = true,
		position = true,
		downReport = {},
		downExport = {},
		exportExcel = false,
		reportName,
		reportExecutionID,
		officePickupID,
		downMultiReport = true,
		downMultiExport = true,
		hasCompetitionSites = false,
		hasDirectSites = false,
		officeNoResults = false,
		idOfficeHistoric = null,
		analyzerOffice = null,
		showHistory = true,
		showMatches = false,
		isExtras = false,
		isRunning = false,
		isRating = false,
		isOneWay = false,
	} = props

	const { handleMultiAction, handleMultiRatingAction, handleCallAction } = useReportActions(reportExecutionID, reportName)

	const buttonsConfig = [
		{
			condition: lor && !officeNoResults && !isExtras && !isRating && !isOneWay,
			icon: "tally",
			tooltip: "LOR Analyzer",
			path: ANALYZERS_ROUTES.LOR_PATH,
			tourId: 'tour-date-btn-lor',
		}, {
			condition: dor && !officeNoResults && !isExtras && !isRating && !isOneWay,
			icon: "calendar-days",
			tooltip: "DOR Analyzer",
			path: ANALYZERS_ROUTES.DOR_PATH,
			sx: { color: "info.main" },
			tourId: 'tour-date-btn-dor',
		}, {
			condition: hasDirectSites && !officeNoResults && !isExtras && !isRating && !isOneWay,
			icon: "chart-line",
			tooltip: "Direct Site Analyzer",
			path: ANALYZERS_ROUTES.DIRECT_SITE_PATH,
			sx: { color: "success.dark" },
			tourId: 'tour-date-btn-direct',
		}, {
			condition: hasCompetitionSites && !officeNoResults && !isExtras && !isRating && !isOneWay,
			icon: "handshake",
			tooltip: "Brokers Analyzer",
			path: ANALYZERS_ROUTES.BROKERS_PATH,
			sx: { color: "warning.main" },
			tourId: 'tour-date-btn-brokers',
		}, {
			condition: position && !officeNoResults && !isExtras && !isRating && !isOneWay,
			icon: "ranking-star",
			tooltip: "Position Analyzer",
			path: ANALYZERS_ROUTES.POSITION_PATH,
			sx: { color: "secondary.main" },
			tourId: 'tour-date-btn-position',
		}, {
			condition: hasCompetitionSites && !officeNoResults && !isExtras && !isOneWay,
			icon: "star",
			tooltip: "Rating Analyzer",
			path: ANALYZERS_ROUTES.RATING_PATH,
			sx: { color: "warning.main" },
			tourId: 'tour-date-btn-rating',
		}, {
			condition: isOneWay && !officeNoResults,
			icon: "arrow-right-arrow-left",
			tooltip: "One Way Analyzer",
			path: ONE_WAY_ROUTES.ANALYZER_PATH,
			sx: { color: "success.dark" },
		}, {
			condition: reportExecutionID && showHistory && !isExtras,
			icon: "clock-rotate-left",
			tooltip: "View in real time historical",
			path: REAL_TIME_ROUTES.HISTORY_PATH,
			tourId: 'tour-date-btn-realtime',
		},
		{
			condition: !isRunning && showMatches && !officeNoResults && !isExtras && !isRating && !isOneWay,
			icon: "file-excel",
			tooltip: "Get custom categories matches",
			action: () => getExcel(`${BACKEND_APIS.CUSTOM_CATEGORY}/matches`, { idReportExecution: reportExecutionID }),
			url: '',
			sx: { color: "colors.deepOrange" },
			tourId: 'tour-date-btn-categories',
		}, {
			condition: !isRunning && showMatches && !officeNoResults && !isExtras && !isRating && !isOneWay,
			icon: "file-excel",
			tooltip: "Get custom acriss matches",
			action: () => getExcel(`${BACKEND_APIS.CUSTOM_ACRISS}/matches`, { idReportExecution: reportExecutionID }),
			url: '',
			sx: { color: "colors.teal" },
			tourId: 'tour-date-btn-acriss',
		}, {
			condition: downReport?.hasGenerate && !isRating,
			icon: "cloud-arrow-down",
			tooltip: "Download report",
			url: downReport?.report_url,
			sx: { color: "success.dark" },
			tourId: 'tour-date-btn-report',
		}, {
			condition: downExport?.hasExport && !isRating,
			icon: "file-arrow-down",
			tooltip: "Download export",
			url: downExport?.export_url,
			sx: { color: "primary.main" },
			tourId: 'tour-date-btn-export',
		}, {
			condition: downMultiReport && reportExecutionID && !isRating,
			icon: "cloud-arrow-down",
			tooltip: "Download multi report",
			action: () => handleMultiAction(REPORT_ACTIONS.MULTI_REPORT),
			url: '',
			sx: {
				color: "colors.pink",
				'&:hover': { backgroundColor: pink[50] },
			},
			tourId: 'tour-date-btn-multireport',
		}, {
			condition: downMultiReport && reportExecutionID && isRating,
			icon: "cloud-arrow-down",
			tooltip: "Download multi report",
			action: () => handleMultiRatingAction(),
			url: '',
			sx: {
				color: "colors.pink",
				'&:hover': { backgroundColor: pink[50] },
			},
		}, {
			condition: downMultiExport && reportExecutionID && !isRating,
			icon: "file-arrow-down",
			tooltip: "Download multi export",
			action: () => handleMultiAction(REPORT_ACTIONS.MULTI_EXPORT),
			url: '',
			sx: {
				color: "colors.deepPurple",
				'&:hover': { backgroundColor: deepPurple[50] },
			},
			tourId: 'tour-date-btn-multiexport',
		}, {
			condition: !!exportExcel && reportExecutionID && !isRating,
			icon: "file-export",
			tooltip: "Export Excel",
			sx: { color: "primary.main" },
			action: () => handleCallAction(REPORT_ACTIONS.EXPORT, officePickupID),
			url: '',
		}
	]

	const handleClick = (url, action, e) => {
		if (url) {
			window.open(url, e?.button === 1 ? undefined : "_blank")
		} else if (action) {
			action()
		}
		e?.preventDefault()
	}

	return (
		<Box sx={{ display: 'flex', gap: .5 }}>
			{buttonsConfig.map(
				({
					 condition,
					 icon,
					 tooltip,
					 path,
					 url = `${path}?idReportExecution=${reportExecutionID}${(analyzerOffice || idOfficeHistoric) ? `&idOfficePickup=${idOfficeHistoric || analyzerOffice}` : ''}`,
					 action,
					 sx,
					 tourId,
				 }, idx) => condition && (
					<AppButtonIcon
						key={`${icon}-${idx}`}
						icon={icon}
						tooltip={tooltip}
						sx={sx}
						onClick={(e) => handleClick(url, action, e)}
						onAuxClick={(e) => handleClick(url, action, e)}
						{...(tourId && { 'data-tour-id': tourId })}
					/>
				)
			)}
		</Box>
	)
}
